import type { CompanyDiscoveryAiOutput } from '@revelio/data-access';

import { getFilterDefinition } from '../../engine/filterRegistry';
import type {
  PrimarySelection,
  TreeFilter,
  TreeSelection,
} from '../../types/filters';
import { FilterId, FilterType } from '../../types/filters';

type ResolvedIndustryItem = {
  name: string;
  relevantPromptText: string;
  id?: string;
  levelId?: string;
  parentK10Id?: string;
  parentK10Name?: string;
};
type ResolvedGeographyItem = {
  name: string;
  relevantPromptText: string;
  id?: string;
  levelId?: string;
};
type ResolvedRoleItem = {
  name: string;
  relevantPromptText: string;
  id?: string;
  selectionListId?: string;
};

const PRIMARY_INDUSTRY_FILTER_ID = FilterId.INDUSTRY;
const PRIMARY_GEOGRAPHY_FILTER_ID = FilterId.GEOGRAPHY;
const PRIMARY_ROLE_FILTER_ID = FilterId.ROLE;

function buildSelectionsFromIndustry(
  industry: NonNullable<
    CompanyDiscoveryAiOutput['filters']['primary_selector']['industry']
  >
): TreeSelection[] {
  const items = industry as ResolvedIndustryItem[];
  const definition = getFilterDefinition(PRIMARY_INDUSTRY_FILTER_ID);
  if (!definition || definition.type !== FilterType.TREE) return [];
  const selectionListIds =
    (definition as { selectionListIds?: string[] }).selectionListIds ?? [];

  // Resolver ids can arrive level-qualified (`rics_k10.8`); the RICS filters key
  // off bare numeric ids, so strip the qualifier. `pop()` on a trailing-dot id
  // yields '', so fall back on falsy rather than nullish.
  const toBareId = (id: string) =>
    id.includes('.') ? id.split('.').pop() || id : id;

  const normalized: { id: string; name: string; levelId: string }[] = [];
  for (const item of items) {
    const levelId = item.levelId ?? selectionListIds[0];
    if (levelId === 'rics_k50' && item.parentK10Id !== undefined) {
      normalized.push({
        id: String(item.parentK10Id),
        name: item.parentK10Name ?? item.name,
        levelId: 'rics_k10',
      });
    } else {
      const id = item.id ?? item.name;
      normalized.push({
        id: toBareId(id),
        name: item.name,
        levelId: levelId as string,
      });
    }
  }

  const seenKey = new Set<string>();
  const selections: TreeSelection[] = [];
  for (const n of normalized) {
    if (!selectionListIds.includes(n.levelId)) continue;
    const key = `${n.levelId}:${n.id}`;
    if (seenKey.has(key)) continue;
    seenKey.add(key);
    selections.push({
      id: n.id,
      name: n.name,
      levelId: n.levelId as TreeSelection['levelId'],
    });
  }
  return selections;
}

function buildSelectionsFromGeography(
  geography: NonNullable<
    CompanyDiscoveryAiOutput['filters']['primary_selector']['geography']
  >
): TreeSelection[] {
  const items = geography as ResolvedGeographyItem[];
  const definition = getFilterDefinition(PRIMARY_GEOGRAPHY_FILTER_ID);
  if (!definition || definition.type !== FilterType.TREE) return [];
  const selectionListIds =
    (definition as { selectionListIds?: string[] }).selectionListIds ?? [];
  const selections: TreeSelection[] = [];
  for (const item of items) {
    const id = item.id ?? item.name;
    const levelId =
      (item.levelId as TreeSelection['levelId']) ?? selectionListIds[0];
    if (selectionListIds.includes(levelId)) {
      selections.push({ id, name: item.name, levelId });
    }
  }
  return selections;
}

function buildSelectionsFromRole(
  role: NonNullable<
    CompanyDiscoveryAiOutput['filters']['primary_selector']['role']
  >
): TreeSelection[] {
  const items = role as ResolvedRoleItem[];
  const definition = getFilterDefinition(PRIMARY_ROLE_FILTER_ID);
  if (!definition || definition.type !== FilterType.TREE) return [];
  const selectionListIds =
    (definition as { selectionListIds?: string[] }).selectionListIds ?? [];
  const selections: TreeSelection[] = [];
  for (const item of items) {
    const id = item.id ?? item.name;
    const levelId = (item.selectionListId ??
      selectionListIds[0]) as TreeSelection['levelId'];
    if (selectionListIds.includes(levelId)) {
      selections.push({ id, name: item.name, levelId });
    }
  }
  return selections;
}

function buildTreeFilter(
  filterId: FilterId,
  selections: TreeSelection[]
): TreeFilter | null {
  if (selections.length === 0) return null;
  const definition = getFilterDefinition(filterId);
  if (!definition || definition.type !== FilterType.TREE) return null;
  const selectionListIds =
    (definition as { selectionListIds?: string[] }).selectionListIds ?? [];
  const primarySelections: PrimarySelection[] = selections.map((sel) => ({
    ...sel,
    isActive: true,
  }));
  return {
    ...definition,
    id: filterId,
    name: definition.name,
    type: FilterType.TREE,
    selectionListIds,
    selections,
    primarySelections,
    // Screener v2 has no `tabContent.primaryFilter`; the filter chip row
    // (`useFilterTags`) only shows filters with isPrimary false. "Primary"
    // in the AI payload names the scoping axis, not CustomDash's primary bar.
    isPrimary: false,
    isUserSubmitted: true,
  } as TreeFilter;
}

/**
 * Converts primary_selector into one TreeFilter per POPULATED dimension, first
 * entry being the scoping axis. The parser routinely resolves more than one
 * ("fastest growing accountancy firms in the UK" → industry + geography), and a
 * page ignores the dimensions its template does not list (`useEffectiveFilters`),
 * so emitting all of them is safe everywhere and emitting only the first loses
 * the rest. Callers needing a specific dimension key off the filter id.
 */
export const transformPrimarySelector = (
  primary_selector: CompanyDiscoveryAiOutput['filters']['primary_selector']
): TreeFilter[] => {
  if (!primary_selector) return [];

  const industry = primary_selector.industry;
  const geography = primary_selector.geography;
  const role = primary_selector.role;

  const filters: TreeFilter[] = [];

  if (Array.isArray(industry) && industry.length > 0) {
    const filter = buildTreeFilter(
      PRIMARY_INDUSTRY_FILTER_ID,
      buildSelectionsFromIndustry(industry)
    );
    if (filter) filters.push(filter);
  }

  if (Array.isArray(geography) && geography.length > 0) {
    const filter = buildTreeFilter(
      PRIMARY_GEOGRAPHY_FILTER_ID,
      buildSelectionsFromGeography(geography)
    );
    if (filter) filters.push(filter);
  }

  if (Array.isArray(role) && role.length > 0) {
    const filter = buildTreeFilter(
      PRIMARY_ROLE_FILTER_ID,
      buildSelectionsFromRole(role)
    );
    if (filter) filters.push(filter);
  }

  return filters;
};
